import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@barges/react';
import classes from './classes.module.scss';
import RemovePaymentSource from './remove_payment_method';
import ExpandedText from '../../helpers/expanded_text';
import CreditCardImg from '../../add_credit_card_form/credit_card_img';
import {
  formatCardName, hideEmailAdress
} from '../../config/utils';
import { PurchaseSvgIcon, SvgIcon } from '../../sprites';
import { applePayIsSupported, googlePayIsSupported } from '../../purchase_details_common/purchase_details_common_utils';

const renderSourceIcon = ({ kind, type }) => {
  if (kind === 'cc') return <CreditCardImg type={ type } className={ classes.sourceIcon } />;
  if (kind === 'ap' || kind === 'am') return <div className={ classes.applePayButton } />;
  if (kind === 'gp') return <PurchaseSvgIcon id="google_pay_icon" className={ classes.sourceIconGP } />;
  return <PurchaseSvgIcon id="paypal_icon" className={ classes.sourceIcon } />;
};

const canNotRemove = ({ isDefault, existing }) => {
  const notSupportedKings = [];
  if (!applePayIsSupported()) {
    notSupportedKings.push('ap');
    notSupportedKings.push('am');
  }
  if (!googlePayIsSupported()) {
    notSupportedKings.push('gp');
  }

  if (notSupportedKings.length > 0) {
    const existingWithoutNotSupportedKings = existing.filter((e) => !notSupportedKings.includes(e.kind));
    return isDefault && existingWithoutNotSupportedKings.length > 1;
  }
  return isDefault && existing.length > 1;
};

const renderRemoveSourceItem = ({ onRemove }) => (
  <button type="button" className={ classes.removeItem } onClick={ onRemove }>
    <PurchaseSvgIcon id="delete_icon" className={ classes.deleteIcon } size={ 60 } />
  </button>
);

const getSourceType = ({ kind, type }) => {
  if (kind === 'ap' || kind === 'am') return 'Apple Pay';
  if (kind === 'gp') return 'Google Pay';
  if (type) return formatCardName(type);
  return 'PayPal';
};

const renderSourceDetailsHeader = ({
  kind, type, onRemove
}) => (
  <div className={ classes.sourceDetailsHeader }>
    <div className={ classes.sourceName }>
      { renderSourceIcon({ kind, type }) }
      { getSourceType({ kind, type }) }
    </div>
    { renderRemoveSourceItem({ onRemove }) }
  </div>
);

const renderDetails = ({
  kind, last4, exp, paypalBillingAgreementEmail, fullName
}) => {
  if (kind === 'ap' || kind === 'am' || kind === 'gp') return null;
  if (kind === 'cc') {
    return (
      <div className={ classes.ccDetailsContainer }>
        <div className={ classes.ccLast4 }>
          {'* * * * '}
          { last4 }
        </div>
        <div className={ classes.ccDetails }>
          {/* <span>FULL NAME</span> */}
          <span>{ fullName }</span>
          <span>{ `EXP: ${ exp }` }</span>
        </div>
      </div>
    );
  }
  return (
    <div className={ classes.ppDetails }>
      <ExpandedText customClasses={ { text: classes.ellipsisPayPalName } } ellipsis>
        { hideEmailAdress(paypalBillingAgreementEmail) || 'PayPal'}
      </ExpandedText>
    </div>
  );
};

function PaymentMethodDetails({
  kind,
  context,
  isDefault,
  id,
  removeSource,
  onMakeDefault,
  existing,
  fullName
}) {
  const { t } = useTranslation();
  const [removing, setRemoving] = useState(false);
  const [showRemovingErrorIfIsDefaultButNotLast, setShowRemovingErrorIfIsDefaultButNotLast] = useState(false);
  const {
    type, last4, exp, paypalBillingAgreementEmail
  } = context || {};

  const onRemove = () => {
    setRemoving(true);
  };

  const onCancelRemove = () => {
    setRemoving(false);
  };

  const onConfirmRemove = () => {
    if (canNotRemove({ isDefault, existing })) {
      setShowRemovingErrorIfIsDefaultButNotLast(true);
      return;
    }
    removeSource(id);
  };

  const removingErrorIfIsDefaultButNotLastErrorOkBtnClick = () => {
    setShowRemovingErrorIfIsDefaultButNotLast(false);
    setRemoving(false);
  };

  const renderDefaultSection = () => {
    if (isDefault) {
      return (
        <div className={ classes.isDefault }>
          { t('purchase.default')}
        </div>
      );
    }
    return (
      <button
        type="button"
        className={ classes.setDefaultButton }
        onClick={ onMakeDefault }
      >
        { t('purchase.set_default') }
      </button>
    );
  };

  const renderPaymentSourceContainer = () => (
    <div className={ classes.container }>
      { renderSourceDetailsHeader({
        kind, type, onRemove
      }) }
      { renderDetails({
        kind, last4, exp, paypalBillingAgreementEmail, fullName
      }) }
      { renderDefaultSection() }
    </div>
  );

  const renderRemovePaymentMethodPopupContent = () => {
    if (showRemovingErrorIfIsDefaultButNotLast) {
      return (
        <div className={ classes.removingErrorIfIsDefaultButNotLastContainer }>
          <SvgIcon id="error_icon" className={ classes.removingErrorIfIsDefaultButNotLastErrorImg } />
          <div className={ classes.removingErrorIfIsDefaultButNotLastErrorText }>
            { t('purchase.you_must_select_a_new_default_payment_method_before_removing_this_one') }
          </div>
          <button type="button" className={ classes.removingErrorIfIsDefaultButNotLastErrorOkBtn } onClick={ removingErrorIfIsDefaultButNotLastErrorOkBtnClick }>
            {t('general.ok')}
          </button>
        </div>
      );
    }
    return (
      <RemovePaymentSource
        onConfirmRemove={ onConfirmRemove }
        onCancelRemove={ onCancelRemove }
      />
    );
  };

  const removePaymentMethodPopup = () => {
    if (!removing) return null;
    return (
      <Popover isOpen centered className={ classes.removePaymentMethodPopup }>
        { renderRemovePaymentMethodPopupContent() }
      </Popover>
    );
  };

  const renderScreenThemeContainer = () => (
    <div className={ classes.rootWallet }>
      { renderPaymentSourceContainer() }
      { removePaymentMethodPopup() }
    </div>
  );

  return renderScreenThemeContainer();
}

export default PaymentMethodDetails;
